import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Building2, Check, ChevronLeft, Edit3 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCompleteProfile } from "@/hooks/useCompleteProfile";
import { CompanyNameInput } from "@/components/auth/CompanyNameInput";
import { toast } from "@/hooks/use-toast";
import { validateCompanyName } from "@/services/field-validation.service";
import { ADVISOR_PRIMARY_GOALS } from "@/constants/advisor-goals";
import { supabase } from "@/integrations/supabase/client";

const industries = ["Technology", "Healthcare", "Finance", "Manufacturing", "Retail", "Construction", "Professional Services", "Real Estate", "Transportation", "Agriculture", "Education", "Hospitality", "Other"];

type Role = "business_owner" | "potential_successor" | "consultant_advisor" | "";

interface FormData {
  role: Role;
  company_name: string;
  business_structure: string;
  industry: string;
  goals: string[];
  primary_goals: string[]; // Unified advisor goals for team_members table
  has_specific_business: string;
  successor_relationship: string;
  advisor_relationship: string;
  client_count: string;
}

const roleDescriptions = {
  business_owner: "I am a Business Owner exploring the future transition of my business.",
  potential_successor: "I am a Potential Successor planning and preparing to take over a business.",
  consultant_advisor: "I am a Consultant supporting a business through succession and transition."
};

export default function CompleteProfile() {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [completedQuestions, setCompletedQuestions] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    role: "",
    company_name: "",
    business_structure: "",
    industry: "",
    goals: [],
    primary_goals: [], // Initialize unified primary goals
    has_specific_business: "",
    successor_relationship: "",
    advisor_relationship: "",
    client_count: ""
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const completeProfile = useCompleteProfile();
  const [isDuplicateCompany, setIsDuplicateCompany] = useState(false);
  // Analytics tracking
  useEffect(() => {
    // Profile completion analytics
  }, [user?.id]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // SECURITY FIX: Check email verification
    if (!user.email_confirmed_at) {
      toast({
        title: "Email verification required",
        description: "Please verify your email before completing your profile.",
        variant: "destructive"
      });
      navigate('/auth/verify-email');
      return;
    }
  }, [user, navigate]);

  const handleRoleSelect = (role: Role) => {
    setFormData(prev => ({
      ...prev,
      role
    }));
    setCompletedQuestions(prev => [...prev, "role_selection"]);
    setCurrentQuestion(2);
  };

  const handleNext = () => {
    const currentQuestionType = getCurrentQuestionForRole();
    setCompletedQuestions(prev => [...prev, currentQuestionType]);
    setCurrentQuestion(prev => prev + 1);
  };

  const handleEditQuestion = (questionType: string) => {
    // Find the question order based on the role
    const questionOrder = getQuestionOrder();
    const questionIndex = questionOrder.indexOf(questionType);
    if (questionIndex !== -1) {
      // Reset to this question
      setCurrentQuestion(questionIndex + 1);
      // Remove this question and all subsequent ones from completed
      setCompletedQuestions(prev => prev.slice(0, questionIndex));
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(prev => prev - 1);
      const questionOrder = getQuestionOrder();
      const currentQuestionType = questionOrder[currentQuestion - 2];
      if (currentQuestionType) {
        setCompletedQuestions(prev => prev.filter(q => q !== currentQuestionType));
      }
    }
  };

  const getQuestionOrder = () => {
    const base = ["role_selection"];
    switch (formData.role) {
      case "business_owner":
        return [...base, "company_name", "business_structure", "industry", "primary_goals"];
      case "potential_successor":
        return [...base, "has_specific_business", "successor_relationship", "primary_goals"];
      case "consultant_advisor":
        return [...base, "advisor_relationship", "client_count", "primary_goals"];
      default:
        return base;
    }
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      goals: checked ? [...prev.goals, value] : prev.goals.filter(goal => goal !== value)
    }));
  };

  const handlePrimaryGoalChange = (value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      primary_goals: checked 
        ? [...prev.primary_goals, value] 
        : prev.primary_goals.filter(goal => goal !== value)
    }));
  };

  const getCurrentQuestionForRole = () => {
    if (currentQuestion === 1) return "role_selection";
    const baseQuestion = currentQuestion - 1;
    switch (formData.role) {
      case "business_owner":
        switch (baseQuestion) {
          case 1:
            return "company_name";
          case 2:
            return "business_structure";
          case 3:
            return "industry";
          case 4:
            return "primary_goals";
          default:
            return "complete";
        }
      case "potential_successor":
        switch (baseQuestion) {
          case 1:
            return "has_specific_business";
          case 2:
            return "successor_relationship";
          case 3:
            return "primary_goals";
          default:
            return "complete";
        }
      case "consultant_advisor":
        switch (baseQuestion) {
          case 1:
            return "advisor_relationship";
          case 2:
            return "client_count";
          case 3:
            return "primary_goals";
          default:
            return "complete";
        }
      default:
        return "role_selection";
    }
  };

  const canProceed = () => {
    const question = getCurrentQuestionForRole();
    switch (question) {
      case "role_selection":
        return formData.role !== "";
      case "company_name":
        return formData.company_name.trim() !== "" && !isDuplicateCompany;
      case "business_structure":
        return formData.business_structure !== "";
      case "industry":
        return formData.industry !== "";
      case "primary_goals":
        return formData.primary_goals.length > 0;
      case "has_specific_business":
        return formData.has_specific_business !== "";
      case "successor_relationship":
        return formData.successor_relationship !== "";
      case "advisor_relationship":
        return formData.advisor_relationship !== "";
      case "client_count":
        return formData.client_count !== "";
      default:
        return false;
    }
  };

  const getQuestionValue = (questionType: string): string => {
    switch (questionType) {
      case "role_selection":
        return formData.role ? roleDescriptions[formData.role] : "";
      case "company_name":
        return formData.company_name;
      case "business_structure":
        const structureLabels = {
          corporation: "Incorporated (Corporation)",
          sole_proprietorship: "Sole Proprietorship",
          partnership: "Partnership (incl. LLP/LP)",
          other: "Not sure / Other"
        };
        return structureLabels[formData.business_structure as keyof typeof structureLabels] || "";
      case "industry":
        return formData.industry;
      case "primary_goals":
        return formData.primary_goals.join(", ");
      case "has_specific_business":
        return formData.has_specific_business === "yes" ? `Yes - ${formData.company_name}` : "Not yet";
      case "successor_relationship":
        return formData.successor_relationship;
      case "advisor_relationship":
        return formData.advisor_relationship;
      case "client_count":
        return formData.client_count;
      default:
        return "";
    }
  };

  const getQuestionTitle = (questionType: string): string => {
    switch (questionType) {
      case "role_selection":
        return "Which best describes you?";
      case "company_name":
        return "What's your company's name?";
      case "business_structure":
        return "How is your business structured?";
      case "industry":
        return "What industry are you in?";
      case "primary_goals":
        return "What are your main goals for using mySuccession?";
      case "has_specific_business":
        return "Are you taking over a specific business?";
      case "successor_relationship":
        return "How would you describe your relationship to the business?";
      case "advisor_relationship":
        return "What best describes your advisory role?";
      case "client_count":
        return "How many clients do you intend on supporting with mySuccession?";
      default:
        return "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      // Get names from user metadata or prompt user to complete
      const firstName = user.user_metadata?.first_name || user.email?.split('@')[0] || 'User';
      const lastName = user.user_metadata?.last_name || '';
      
      if (formData.role === "") {
        throw new Error("Please select a role");
      }

      // Complete profile via RPC function
      await completeProfile.mutateAsync({
        role: formData.role as 'business_owner' | 'potential_successor' | 'consultant_advisor',
        legal_first_name: firstName,
        legal_last_name: lastName,
        company_name: formData.company_name || undefined,
        business_structure: formData.business_structure || undefined,
        industry: formData.industry || undefined,
        goals: formData.goals.length > 0 ? formData.goals : undefined,
        has_specific_business: formData.has_specific_business || undefined,
        successor_relationship: formData.successor_relationship || undefined,
        advisor_relationship: formData.advisor_relationship || undefined,
        client_count: formData.client_count || undefined
      });

      // Save primary_goals to team_members table
      if (formData.primary_goals.length > 0) {
        const { error: goalsError } = await supabase
          .from('team_members')
          .update({ primary_goals: formData.primary_goals })
          .eq('user_id', user.id);

        if (goalsError) {
          toast({
            title: "Warning",
            description: "Profile created but goals couldn't be saved. You can update them later.",
            variant: "default"
          });
        }
      }

      // SECURITY: Re-fetch session to ensure we have the current user's context
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession?.user || freshSession.user.id !== user.id) {
        toast({
          title: "Session Error",
          description: "Your session has changed. Please sign in again.",
          variant: "destructive"
        });
        navigate('/login', { replace: true });
        return;
      }

      // Navigate to dashboard immediately after success toast
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: { context: 'profile_error', operation: 'complete_profile', component: 'CompleteProfile' },
        extra: { message: 'Profile completion error' }
      });
      // Error handling is done in the hook
    }
  };

  const renderProgressStep = (questionType: string, index: number) => {
    const isCompleted = completedQuestions.includes(questionType);
    const isCurrent = getCurrentQuestionForRole() === questionType;
    const value = getQuestionValue(questionType);
    const title = getQuestionTitle(questionType);
    
    // Only show if completed or current question
    if (!isCompleted && !isCurrent) return null;
    
    return (
      <div key={questionType} className="flex items-start gap-3">
        <div className="flex flex-col items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              isCompleted 
                ? 'bg-primary text-primary-foreground' 
                : isCurrent 
                  ? 'bg-primary/20 text-primary border-2 border-primary' 
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
          </div>
          {(isCompleted || index < currentQuestion - 1) && index < getQuestionOrder().length - 1 && (
            <div className={`w-0.5 h-8 mt-2 ${isCompleted ? 'bg-primary' : 'bg-muted'}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0 pb-8">
          <h3 className={`text-sm font-medium mb-1 ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
            {title}
          </h3>
          {isCompleted && value && (
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-foreground break-words whitespace-normal">{value}</p>
              <button
                onClick={() => handleEditQuestion(questionType)}
                className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                title="Edit answer"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            </div>
          )}
          {isCurrent && !isCompleted && (
            <p className="text-sm text-muted-foreground">Current question</p>
          )}
        </div>
      </div>
    );
  };

  const renderActiveQuestion = () => {
    const question = getCurrentQuestionForRole();
    
    switch (question) {
      case "role_selection":
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-foreground mb-2 font-semibold text-left text-lg">
                Which best describes you?
              </h2>
            </div>

            <div className="space-y-4">
              <div className="text-lg text-foreground mb-4">
                I am a{" "}
                <Select onValueChange={handleRoleSelect}>
                  <SelectTrigger className="inline-flex w-auto min-w-[200px] bg-background">
                    <SelectValue placeholder="[select role]" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business_owner">Business Owner</SelectItem>
                    <SelectItem value="potential_successor">Potential Successor</SelectItem>
                    <SelectItem value="consultant_advisor">Consultant or Advisor</SelectItem>
                  </SelectContent>
                </Select>
                {formData.role && " ..."}
              </div>

              {formData.role && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg">
                  <p className="text-foreground font-medium">
                    {roleDescriptions[formData.role]}
                  </p>
                </div>
              )}

              {formData.role && (
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Don't worry — you can still switch roles later if things change.
                </p>
              )}
            </div>
          </div>
        );

      case "company_name":
        return (
          <CompanyNameInput
            value={formData.company_name}
            onChange={(value) => setFormData({ ...formData, company_name: value })}
            onDuplicateDetected={(isDuplicate) => setIsDuplicateCompany(isDuplicate)}
          />
        );

      case "business_structure":
        return (
          <div className="space-y-4">
            <h2 className="font-semibold text-foreground text-lg">
              How is your business structured?
            </h2>
            <RadioGroup 
              value={formData.business_structure} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, business_structure: value }))}
              className="space-y-3"
            >
              {[
                { value: "corporation", label: "Incorporated (Corporation)" },
                { value: "sole_proprietorship", label: "Sole Proprietorship" },
                { value: "partnership", label: "Partnership (incl. LLP/LP)" },
                { value: "other", label: "Not sure / Other" }
              ].map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={option.value} />
                  <Label htmlFor={option.value} className="text-base">{option.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case "industry":
        return (
          <div className="space-y-4">
            <h2 className="font-semibold text-foreground text-lg">
              What industry are you in?
            </h2>
            <Select 
              value={formData.industry} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "primary_goals":
        return (
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold text-foreground text-lg mb-2">
                What are your main goals for using mySuccession? *
              </h2>
              <p className="text-sm text-muted-foreground">
                Select all that apply. This helps us personalize your experience.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ADVISOR_PRIMARY_GOALS.map((goal) => (
                <div key={goal} className="flex items-start space-x-3">
                  <Checkbox
                    id={goal}
                    checked={formData.primary_goals.includes(goal)}
                    onCheckedChange={(checked) => handlePrimaryGoalChange(goal, checked as boolean)}
                  />
                  <Label 
                    htmlFor={goal} 
                    className="text-sm leading-tight cursor-pointer font-normal"
                  >
                    {goal}
                  </Label>
                </div>
              ))}
            </div>
            {formData.primary_goals.length === 0 && (
              <p className="text-sm text-destructive mt-2">
                Please select at least one goal to continue
              </p>
            )}
          </div>
        );

      case "has_specific_business":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              Are you taking over a specific business?
            </h2>
            <RadioGroup 
              value={formData.has_specific_business} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, has_specific_business: value }))}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes" className="text-base">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no" className="text-base">Not yet</Label>
              </div>
            </RadioGroup>
            
            {formData.has_specific_business === "yes" && (
              <div className="mt-4">
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="What's the name of the business?"
                  className="text-lg"
                />
              </div>
            )}
          </div>
        );

      case "successor_relationship":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              How would you describe your relationship to the business?
            </h2>
            <RadioGroup 
              value={formData.successor_relationship} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, successor_relationship: value }))}
              className="space-y-3"
            >
              {[
                "Family member stepping into the business",
                "Key employee preparing for ownership",
                "External buyer or investor",
                "Advisor supporting the transition",
                "Other"
              ].map((role) => (
                <div key={role} className="flex items-center space-x-2">
                  <RadioGroupItem value={role} id={role} />
                  <Label htmlFor={role} className="text-base">{role}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );


      case "advisor_relationship":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              What best describes your advisory role?
            </h2>
            <RadioGroup 
              value={formData.advisor_relationship} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, advisor_relationship: value }))}
              className="space-y-3"
            >
              {[
                "Exit planning advisor",
                "Accountant / CPA",
                "Lawyer / Legal advisor",
                "M&A professional / Broker",
                "Financial planner / Wealth advisor",
                "Other"
              ].map((role) => (
                <div key={role} className="flex items-center space-x-2">
                  <RadioGroupItem value={role} id={role} />
                  <Label htmlFor={role} className="text-base">{role}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case "client_count":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">
              How many clients do you intend on supporting with mySuccession?
            </h2>
            <RadioGroup 
              value={formData.client_count} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, client_count: value }))}
              className="space-y-3"
            >
              {["Just 1", "A few (2–5)", "Several (6–15)", "Many (15+)"].map((count) => (
                <div key={count} className="flex items-center space-x-2">
                  <RadioGroupItem value={count} id={count} />
                  <Label htmlFor={count} className="text-base">{count}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );


      default:
        return null;
    }
  };

  const getMaxQuestions = () => {
    switch (formData.role) {
      case "business_owner":
        return 5;
      case "potential_successor":
        return 4;
      case "consultant_advisor":
        return 4;
      default:
        return 1;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Complete Your Profile</h1>
            <p className="text-muted-foreground">
              Just a few quick questions so we can personalize the build of your dashboard, guides and support resources.
            </p>
          </div>

          {/* Progress Summary */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Profile Setup</CardTitle>
                <div className="text-sm text-muted-foreground">
                  {completedQuestions.length} of {getMaxQuestions()} completed
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2 mt-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(completedQuestions.length / getMaxQuestions()) * 100}%` }}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {getQuestionOrder().map((questionType, index) => 
                  renderProgressStep(questionType, index)
                )}
              </div>
            </CardContent>
          </Card>

          {/* Active Question or Completion */}
          {getCurrentQuestionForRole() === "complete" ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold text-foreground mb-2">We're Ready to Go!</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for the profile information. Let's get started with your succession planning journey.
                  </p>
                  <form onSubmit={handleSubmit}>
                    <Button
                      type="submit"
                      disabled={completeProfile.isPending}
                      className="w-full max-w-xs"
                    >
                      {completeProfile.isPending ? "Getting Started..." : "Get Started"}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                {renderActiveQuestion()}
                
                <div className="flex gap-3 mt-8">
                  {currentQuestion > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="flex-1"
                  >
                    {currentQuestion < getMaxQuestions() ? "Continue" : "Continue"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}